import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, ShieldCheck, Loader2, Eye, Check, Trash2, Plus, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";

/**
 * Admin-only panel rendered inside the Profile page. It supports:
 * — live-fetching the OpenRouter catalog every mount (no client cache);
 * — swapping the model in each ACTIVE slot via a per-slot <select>;
 * — REMOVING an active slot (with a min-1 guard on the server);
 * — ADDING a new provider slot from the "Add another AI engine" card.
 */
export default function AdminModelConfig() {
  const { t } = useLang();
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [catalog, setCatalog] = useState(null);       // { fetched_at, slots:[{id,provider,prefix,active,options:[...]}] }
  const [current, setCurrent] = useState(null);       // { slots:[{id,provider,prefix,model,label,vision}] }
  const [selection, setSelection] = useState({});     // { slotId: modelId }
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");

  // Add-slot form state
  const [addOpen, setAddOpen] = useState(false);
  const [addProvider, setAddProvider] = useState("");
  const [addModel, setAddModel] = useState("");
  const [adding, setAdding] = useState(false);

  const loadAll = useCallback(async () => {
    setLoadingCatalog(true);
    setError("");
    try {
      const [{ data: cur }, { data: cat }] = await Promise.all([
        api.get("/admin/models"),
        api.get("/admin/openrouter/catalog"),
      ]);
      setCurrent(cur);
      setCatalog(cat);
      const initSel = {};
      cur.slots.forEach((s) => { initSel[s.id] = s.model; });
      setSelection(initSel);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || t("admin.catalogError"));
    } finally {
      setLoadingCatalog(false);
    }
  }, [t]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const catalogBySlot = useMemo(() => {
    const map = {};
    (catalog?.slots || []).forEach((s) => { map[s.id] = s; });
    return map;
  }, [catalog]);

  const availableProviders = useMemo(
    () => (catalog?.slots || []).filter((s) => !s.active),
    [catalog]
  );

  const dirtyCount = useMemo(() => {
    if (!current) return 0;
    return current.slots.reduce((n, s) => n + (selection[s.id] && selection[s.id] !== s.model ? 1 : 0), 0);
  }, [current, selection]);

  const broadcast = (slots) => {
    const payload = slots.map((s) => ({ id: s.id, label: s.label, provider: s.provider, model: s.model }));
    try { window.dispatchEvent(new CustomEvent("pq:models-updated", { detail: payload })); } catch { /* ignore */ }
    try {
      const bc = new BroadcastChannel("prismquery");
      bc.postMessage({ type: "models-updated", detail: payload });
      bc.close();
    } catch { /* ignore */ }
  };

  const applyChanges = async () => {
    if (dirtyCount === 0) {
      toast.message(t("admin.noChanges"));
      return;
    }
    setApplying(true);
    try {
      const slots = {};
      current.slots.forEach((s) => {
        const chosen = selection[s.id];
        if (chosen && chosen !== s.model) {
          const opt = (catalogBySlot[s.id]?.options || []).find((o) => o.id === chosen);
          slots[s.id] = {
            model: chosen,
            label: opt?.name || chosen.split("/", 2)[1] || chosen,
            vision: !!opt?.supports_vision,
          };
        }
      });
      const { data } = await api.put("/admin/models", { slots });
      setCurrent({ slots: data.slots });
      const nextSel = {};
      data.slots.forEach((s) => { nextSel[s.id] = s.model; });
      setSelection(nextSel);
      broadcast(data.slots);
      toast.success(t("admin.applied"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("admin.applyError"));
    } finally {
      setApplying(false);
    }
  };

  const removeSlot = async (slotId) => {
    if ((current?.slots?.length || 0) <= 1) return;
    try {
      const { data } = await api.delete(`/admin/models/slots/${slotId}`);
      setCurrent({ slots: data.slots });
      const nextSel = {};
      data.slots.forEach((s) => { nextSel[s.id] = s.model; });
      setSelection(nextSel);
      // Refresh catalog to flip the removed slot's active flag.
      loadAll();
      broadcast(data.slots);
      toast.success(t("admin.slotRemoved"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("admin.removeError"));
    }
  };

  const openAdd = () => {
    setAddOpen(true);
    if (availableProviders.length > 0) {
      setAddProvider(availableProviders[0].id);
      setAddModel(availableProviders[0].options[0]?.id || "");
    }
  };

  const cancelAdd = () => {
    setAddOpen(false);
    setAddProvider("");
    setAddModel("");
  };

  const submitAdd = async () => {
    if (!addProvider || !addModel) return;
    setAdding(true);
    try {
      const opt = (catalogBySlot[addProvider]?.options || []).find((o) => o.id === addModel);
      const { data } = await api.post("/admin/models/slots", {
        slot_id: addProvider,
        model: addModel,
        label: opt?.name || addModel.split("/", 2)[1] || addModel,
        vision: !!opt?.supports_vision,
      });
      setCurrent({ slots: data.slots });
      const nextSel = {};
      data.slots.forEach((s) => { nextSel[s.id] = s.model; });
      setSelection(nextSel);
      loadAll();
      broadcast(data.slots);
      toast.success(t("admin.slotAdded"));
      cancelAdd();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("admin.addError"));
    } finally {
      setAdding(false);
    }
  };

  const activeSlotsCount = current?.slots?.length || 0;
  const fetchedAtLabel = catalog?.fetched_at
    ? new Date(catalog.fetched_at).toLocaleTimeString()
    : "";
  const addOptions = (catalogBySlot[addProvider]?.options) || [];

  return (
    <div data-testid="admin-model-config">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-4 h-4 text-[#F5C542]" />
        <h2 className="label-eyebrow text-[#F5C542]">{t("admin.section")}</h2>
      </div>
      <p className="text-[13px] text-white/60 mb-6 max-w-lg">
        {t("admin.description")}
      </p>

      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={loadAll}
          disabled={loadingCatalog}
          className="btn-ghost focus-ring"
          data-testid="admin-refresh-catalog"
        >
          {loadingCatalog ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}
          <span className="text-[11px] uppercase tracking-[0.15em]">
            {loadingCatalog ? t("admin.refreshing") : t("admin.refresh")}
          </span>
        </button>
        {fetchedAtLabel && (
          <span className="label-eyebrow opacity-60" data-testid="admin-catalog-fetched-at">
            {t("admin.fetchedAt", { time: fetchedAtLabel })}
          </span>
        )}
      </div>

      {error && (
        <div className="text-[12px] font-mono-p text-[#ff9c85] border border-[#ff9c85]/30 rounded px-3 py-2 bg-[#ff9c85]/5 mb-6" data-testid="admin-error">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {(current?.slots || []).map((slot) => {
          const opts = catalogBySlot[slot.id]?.options || [];
          const optionsCount = opts.length;
          const selected = selection[slot.id] || slot.model;
          const changed = selected !== slot.model;
          const selectedOpt = opts.find((o) => o.id === selected);
          const canRemove = activeSlotsCount > 1;
          return (
            <div
              key={slot.id}
              className={`p-4 rounded-md border transition-colors ${
                changed
                  ? "border-[#F5C542]/50 bg-[#F5C542]/[0.04]"
                  : "border-white/10 bg-white/[0.02]"
              }`}
              data-testid={`admin-slot-${slot.id}`}
            >
              <div className="flex items-baseline justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="label-eyebrow text-white/70">{slot.provider}</div>
                  <div className="text-[13px] text-white/90 font-mono-p truncate" data-testid={`admin-slot-${slot.id}-current`}>
                    {t("admin.currentModel")}: <span className="text-white">{slot.model}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {slot.vision && (
                    <span className="label-eyebrow flex items-center gap-1 text-white/60">
                      <Eye className="w-3 h-3" /> {t("admin.vision")}
                    </span>
                  )}
                  <span className="label-eyebrow opacity-60">
                    {t("admin.optionsCount", { count: optionsCount })}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeSlot(slot.id)}
                    disabled={!canRemove}
                    className="btn-ghost !h-7 !px-2 focus-ring disabled:opacity-30"
                    title={canRemove ? t("admin.removeSlot") : t("admin.cannotRemoveLast")}
                    aria-label={t("admin.removeSlot")}
                    data-testid={`admin-remove-${slot.id}`}
                  >
                    <Trash2 className="w-3 h-3 text-[#ff9c85]" />
                  </button>
                </div>
              </div>

              <label className="label-eyebrow block mb-1.5" htmlFor={`admin-select-${slot.id}`}>
                {t("admin.chooseModel")}
              </label>
              <div className="relative">
                <select
                  id={`admin-select-${slot.id}`}
                  value={selected}
                  disabled={loadingCatalog || opts.length === 0}
                  onChange={(e) => setSelection((s) => ({ ...s, [slot.id]: e.target.value }))}
                  className="cmd-input w-full h-10 px-3 pr-9 rounded-md text-[13px] font-mono-p appearance-none cursor-pointer disabled:opacity-50"
                  style={{ colorScheme: "dark" }}
                  data-testid={`admin-select-${slot.id}`}
                >
                  {opts.length === 0 && (
                    <option value={slot.model} style={{ backgroundColor: "#0F0F0F", color: "#fff" }}>
                      {slot.model}
                    </option>
                  )}
                  {opts.map((o) => (
                    <option key={o.id} value={o.id} style={{ backgroundColor: "#0F0F0F", color: "#fff" }}>
                      {o.name}{o.supports_vision ? "  · vision" : ""}
                      {o.context_length ? `  · ${Math.round(o.context_length / 1000)}k ctx` : ""}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white/40 text-[10px]">▾</div>
              </div>
              {changed && (
                <div className="mt-2 text-[11px] text-[#F5C542] font-mono-p flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  {slot.model} → <span className="text-white">{selectedOpt?.id || selected}</span>
                </div>
              )}
            </div>
          );
        })}

        {/* ============================================================
             Add-another-AI-engine card
             ============================================================ */}
        {availableProviders.length > 0 && (
          <div
            className="p-4 rounded-md border border-dashed border-white/15 bg-white/[0.015]"
            data-testid="admin-add-slot-card"
          >
            {!addOpen ? (
              <button
                type="button"
                onClick={openAdd}
                className="w-full flex items-center justify-center gap-2 h-10 rounded-md hover:bg-white/[0.03] transition-colors focus-ring text-[13px] text-white/80"
                data-testid="admin-add-slot-open"
              >
                <Plus className="w-3.5 h-3.5" />
                {t("admin.addSlot")}
                <span className="label-eyebrow opacity-60">
                  ({availableProviders.length} {t("admin.available")})
                </span>
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-baseline justify-between">
                  <div className="label-eyebrow text-white/80">{t("admin.addSlotTitle")}</div>
                  <button
                    type="button"
                    onClick={cancelAdd}
                    className="btn-ghost !h-7 !px-2 focus-ring"
                    data-testid="admin-add-slot-cancel"
                    aria-label={t("common.close")}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="add-provider" className="label-eyebrow block mb-1.5">
                      {t("admin.provider")}
                    </label>
                    <select
                      id="add-provider"
                      value={addProvider}
                      onChange={(e) => {
                        setAddProvider(e.target.value);
                        const first = (catalogBySlot[e.target.value]?.options || [])[0];
                        setAddModel(first?.id || "");
                      }}
                      className="cmd-input w-full h-10 px-3 pr-9 rounded-md text-[13px] font-mono-p appearance-none cursor-pointer"
                      style={{ colorScheme: "dark" }}
                      data-testid="admin-add-provider-select"
                    >
                      {availableProviders.map((p) => (
                        <option key={p.id} value={p.id} style={{ backgroundColor: "#0F0F0F", color: "#fff" }}>
                          {p.provider} ({p.options.length})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="add-model" className="label-eyebrow block mb-1.5">
                      {t("admin.chooseModel")}
                    </label>
                    <select
                      id="add-model"
                      value={addModel}
                      onChange={(e) => setAddModel(e.target.value)}
                      disabled={addOptions.length === 0}
                      className="cmd-input w-full h-10 px-3 pr-9 rounded-md text-[13px] font-mono-p appearance-none cursor-pointer disabled:opacity-50"
                      style={{ colorScheme: "dark" }}
                      data-testid="admin-add-model-select"
                    >
                      {addOptions.length === 0 && (
                        <option value="" style={{ backgroundColor: "#0F0F0F", color: "#fff" }}>—</option>
                      )}
                      {addOptions.map((o) => (
                        <option key={o.id} value={o.id} style={{ backgroundColor: "#0F0F0F", color: "#fff" }}>
                          {o.name}{o.supports_vision ? "  · vision" : ""}
                          {o.context_length ? `  · ${Math.round(o.context_length / 1000)}k ctx` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={submitAdd}
                  disabled={adding || !addProvider || !addModel}
                  className="btn-pill focus-ring"
                  data-testid="admin-add-slot-submit"
                >
                  {adding ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>{t("admin.addingSlot")}</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-3.5 h-3.5" />
                      <span>{t("admin.confirmAdd")}</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={applyChanges}
        disabled={applying || loadingCatalog || dirtyCount === 0}
        className="btn-pill focus-ring mt-6"
        data-testid="admin-apply-btn"
      >
        {applying ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>{t("admin.applying")}</span>
          </>
        ) : (
          <>
            <span>{t("admin.apply")}</span>
            {dirtyCount > 0 && (
              <span className="ml-2 text-[10px] font-mono-p bg-white/20 rounded px-1.5 py-0.5" data-testid="admin-dirty-count">
                {dirtyCount}
              </span>
            )}
          </>
        )}
      </button>
    </div>
  );
}
